import { alpha, Box, BoxProps, SxProps, Theme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  CORNER_RESIZE_HANDLE_SIZE,
  GRID_LINE_WIDTH,
  INTERACTIVE_AREA_SIZE,
} from './constants';
import { useGridContext } from './GridContext';
import { useDraggableInteraction } from './useDraggableInteraction';
import { useResizeClamps } from './useResizeClamps';
import { resizeGridByEdge, ResizingEdge } from './utils';

type CornerHandleProps = BoxProps & {
  isDragging: boolean;
  interactionInProgress: boolean;
};

const CornerHandle = ({
  isDragging,
  interactionInProgress,
  ...props
}: CornerHandleProps) => {
  return (
    <Box
      {...props}
      sx={{
        position: 'absolute',
        width: CORNER_RESIZE_HANDLE_SIZE,
        height: CORNER_RESIZE_HANDLE_SIZE,
        backgroundColor: theme => theme.palette.common.white,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: theme => theme.palette.primary.main,
        borderRadius: borderRadius('xl'),
        display: 'inline-block',
        [`&:before`]: {
          content: '""',
          width: CORNER_RESIZE_HANDLE_SIZE + INTERACTIVE_AREA_SIZE,
          height: CORNER_RESIZE_HANDLE_SIZE + INTERACTIVE_AREA_SIZE,
          position: 'absolute',
          // -2 is to compensate for parent border
          transform: `translate(calc(-50% + ${
            CORNER_RESIZE_HANDLE_SIZE / 2 - 2
          }px), calc(-50% + ${CORNER_RESIZE_HANDLE_SIZE / 2 - 2}px))`,
          borderRadius: borderRadius('xl'),
          backgroundColor: theme =>
            isDragging
              ? alpha(theme.palette.background.paper, 0.1)
              : 'transparent',
          transition: theme =>
            theme.transitions.create(['background-color'], {
              duration: theme.transitions.duration.short,
            }),
          zIndex: -1,
        },
        [`&:hover`]: {
          [`&:before`]: {
            backgroundColor: theme =>
              alpha(theme.palette.background.paper, 0.1),
          },
        },
        ...props.sx,
      }}
      style={{
        pointerEvents: interactionInProgress && !isDragging ? 'none' : 'all',
        ...(isDragging ? { cursor: 'grabbing' } : {}),
        ...props.style,
      }}
    />
  );
};

type EdgeHandleProps = BoxProps & {
  isDragging: boolean;
  interactionInProgress: boolean;
  orientation: 'horizontal' | 'vertical';
};

const EdgeHandle = ({
  isDragging,
  orientation,
  interactionInProgress,
  ...props
}: EdgeHandleProps) => {
  return (
    <Box
      {...props}
      sx={{
        ...props.sx,
        borderColor: theme =>
          isDragging
            ? alpha(theme.palette.background.paper, 0.1)
            : 'transparent',
        borderStyle: 'solid',
        borderWidth:
          orientation === 'horizontal'
            ? `${INTERACTIVE_AREA_SIZE}px 0`
            : `0 ${INTERACTIVE_AREA_SIZE}px`,
        backgroundClip: 'content-box',
        position: 'absolute',
        zIndex: -1,
        transition: theme =>
          theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.short,
          }),
        [`&:hover`]: {
          borderColor: theme => alpha(theme.palette.background.paper, 0.1),
        },
      }}
      style={{
        pointerEvents: interactionInProgress && !isDragging ? 'none' : 'all',
        ...(isDragging ? { cursor: 'grabbing' } : {}),
      }}
    />
  );
};

const edgeHandleSxProps: Partial<Record<ResizingEdge, SxProps<Theme>>> = {
  right: {
    height: '100%',
    width: GRID_LINE_WIDTH + 2 * INTERACTIVE_AREA_SIZE,
    right: -GRID_LINE_WIDTH - INTERACTIVE_AREA_SIZE,
    cursor: 'col-resize',
  },
  bottom: {
    height: GRID_LINE_WIDTH + 2 * INTERACTIVE_AREA_SIZE,
    width: '100%',
    bottom: -GRID_LINE_WIDTH - INTERACTIVE_AREA_SIZE,
    cursor: 'row-resize',
  },
  top: {
    height: GRID_LINE_WIDTH + 2 * INTERACTIVE_AREA_SIZE,
    width: '100%',
    top: -GRID_LINE_WIDTH - INTERACTIVE_AREA_SIZE,
    cursor: 'row-resize',
  },
  left: {
    height: '100%',
    width: GRID_LINE_WIDTH + 2 * INTERACTIVE_AREA_SIZE,
    left: -GRID_LINE_WIDTH - INTERACTIVE_AREA_SIZE,
    cursor: 'col-resize',
  },
};

const cornerHandleSxProps: Partial<Record<ResizingEdge, SxProps<Theme>>> = {
  'top-left': {
    left: `-${CORNER_RESIZE_HANDLE_SIZE / 2 + GRID_LINE_WIDTH / 2}px`,
    top: `-${CORNER_RESIZE_HANDLE_SIZE / 2 + GRID_LINE_WIDTH / 2}px`,
    cursor: 'nwse-resize',
  },
  'top-right': {
    left: `calc(100% - ${
      CORNER_RESIZE_HANDLE_SIZE / 2 - GRID_LINE_WIDTH / 2
    }px)`,
    top: `-${CORNER_RESIZE_HANDLE_SIZE / 2 + GRID_LINE_WIDTH / 2}px`,
    cursor: 'nesw-resize',
  },
  'bottom-right': {
    left: `calc(100% - ${
      CORNER_RESIZE_HANDLE_SIZE / 2 - GRID_LINE_WIDTH / 2
    }px)`,
    top: `calc(100% - ${
      CORNER_RESIZE_HANDLE_SIZE / 2 - GRID_LINE_WIDTH / 2
    }px)`,
    cursor: 'nwse-resize',
  },
  'bottom-left': {
    left: `-${CORNER_RESIZE_HANDLE_SIZE / 2 + GRID_LINE_WIDTH / 2}px`,
    top: `calc(100% - ${
      CORNER_RESIZE_HANDLE_SIZE / 2 - GRID_LINE_WIDTH / 2
    }px)`,
    cursor: 'nesw-resize',
  },
};

export type ResizeHandleProps = BoxProps & {
  onResized: (edge: ResizingEdge) => void;
  edge: ResizingEdge;
};

export const ResizeHandle = ({
  edge,
  onResized,
  ...restBoxProps
}: ResizeHandleProps) => {
  const {
    gridState,
    gridDraftState: [_, setDraftState],
    interactionInProgress,
  } = useGridContext();

  const clamp = useResizeClamps(edge);

  const { handleMouseDown, isDragging } = useDraggableInteraction({
    onDragStart: () => setDraftState(gridState),
    onDrag: diff =>
      setDraftState(resizeGridByEdge(gridState, edge, clamp(diff))),
    onDragEnd: () => {
      onResized(edge);
      setDraftState(null);
    },
  });

  if (edge.split('-').length > 1) {
    return (
      <CornerHandle
        isDragging={isDragging}
        interactionInProgress={interactionInProgress}
        onMouseDownCapture={handleMouseDown}
        sx={cornerHandleSxProps[edge]}
        {...restBoxProps}
      />
    );
  }

  return (
    <EdgeHandle
      orientation={
        edge === 'top' || edge === 'bottom' ? 'horizontal' : 'vertical'
      }
      interactionInProgress={interactionInProgress}
      isDragging={isDragging}
      onMouseDownCapture={handleMouseDown}
      sx={edgeHandleSxProps[edge]}
      {...restBoxProps}
    />
  );
};
